import { Table, CombinedTable, Reservation, TableAvailability, TableWeekdayAvailability } from "@/types/table";
import { TablePosition } from "@/hooks/useFloorPlanPositions";
import { FloorPlanZone } from "@/hooks/useFloorPlanZones";
import { FloorPlanTable } from "../FloorPlanTable";
import { FloorPlanFixture } from "../FloorPlanFixture";
import { FloorPlanFixture as FloorPlanFixtureType } from "@/types/fixture";
import {
  getTableStatus,
  getLinkedTableInfo,
  TimeConflictOptions,
  TableShape,
} from "@/lib/floorPlanUtils";
import { cn } from "@/lib/utils";
import { EditableFloorPlanTable } from "./EditableFloorPlanTable";
import { FloorPlanEditorToolbar } from "./FloorPlanEditorToolbar";

interface TimeSlot {
  start: string;
  end: string;
}

interface FloorPlanCanvasProps {
  isExpanded?: boolean;
  // Viewport
  containerRef: React.RefObject<HTMLDivElement>;
  cellSize: number;
  baseCellSize: number;
  panOffset: { x: number; y: number };
  isPanning: boolean;
  isEditMode: boolean;
  effectiveGrid: { cols: number; rows: number };
  gridCols: number;
  gridRows: number;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseUp?: () => void;
  onWheel?: (e: React.WheelEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: () => void;
  isTablet?: boolean;

  // Data
  displayedPositions: TablePosition[];
  displayedFixtures: FloorPlanFixtureType[];
  tables: Table[];
  combinedTables: CombinedTable[];
  dateReservations: Reservation[];
  selectedTimeSlot: string | null;
  timeSlots: TimeSlot[];
  currentTime: Date;
  reservationSettings: { bookingMode: string; reservationDurationMinutes: number } | null;

  // Drag state (reservation drag, non-edit mode)
  isDragging: boolean;
  validDropTargets: Set<string>;
  swapDropTargets: Set<string>;
  combinedDropTargetInfo: Map<string, string>;
  highlightedTableId: string | null;

  // Edit mode
  freeMovementMode: boolean;
  localCheckCollision: (pos: TablePosition, excludeId?: string) => boolean;
  zones: FloorPlanZone[];

  // Fixtures
  selectedFixtureId: string | null;
  onFixtureSelect: (id: string | null) => void;
  onFixturePositionChange: (fixture: FloorPlanFixtureType) => void;
  onFixtureDelete: (id: string) => void;

  // Handlers
  onPositionChange: (pos: TablePosition) => void;
  onEditReservation?: (reservation: Reservation) => void;
  onStatusChange?: (reservation: Reservation, status: "arrivé" | "annulé" | "planifié") => void;
  onDropOnTable: (tableId: string, tableType: 'single' | 'combined') => void;
  onAssignToZone: (tableId: string, zoneId: string | null) => void;
  onFloorPlanClick: (e: React.MouseEvent) => void;

  // Editor mode props (new)
  editorHandlers?: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
  };
  selectedEditTableId?: string | null;
  editDragTransform?: { tableId: string; dx: number; dy: number } | null;
  editResizeTransform?: { tableId: string; dw: number; dh: number } | null;
  onShapeChange?: (tableId: string, shape: TableShape) => void;
  onRotate?: (tableId: string) => void;
  selectedZoneId?: string | null;
  zoneBounds?: Map<string, { x: number; y: number; width: number; height: number }>;
  /** IDs des tables en chevauchement — affichées avec un cadre rouge en édition */
  overlappingTableIds?: Set<string>;
}

export const FloorPlanCanvas = ({
  isExpanded = false,
  containerRef,
  cellSize,
  baseCellSize,
  panOffset,
  isPanning,
  isEditMode,
  effectiveGrid,
  gridCols,
  gridRows,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onWheel,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  isTablet,
  displayedPositions,
  displayedFixtures,
  tables,
  combinedTables,
  dateReservations,
  selectedTimeSlot,
  timeSlots,
  currentTime,
  reservationSettings,
  isDragging,
  validDropTargets,
  swapDropTargets,
  combinedDropTargetInfo,
  highlightedTableId,
  freeMovementMode,
  localCheckCollision,
  zones,
  selectedFixtureId,
  onFixtureSelect,
  onFixturePositionChange,
  onFixtureDelete,
  onPositionChange,
  onEditReservation,
  onStatusChange,
  onDropOnTable,
  onAssignToZone,
  onFloorPlanClick,
  editorHandlers,
  selectedEditTableId,
  editDragTransform,
  editResizeTransform,
  onShapeChange,
  onRotate,
  selectedZoneId,
  zoneBounds,
  overlappingTableIds,
}: FloorPlanCanvasProps) => {
  // Find selected table for toolbar
  const selectedPos = selectedEditTableId
    ? displayedPositions.find(p => p.tableId === selectedEditTableId)
    : null;
  const selectedTable = selectedPos
    ? tables.find(t => t.id === selectedPos.tableId)
    : null;

  // Dynamic height: expanded mode uses full container, otherwise capped
  const containerStyle: React.CSSProperties = isExpanded
    ? {
        width: '100%',
        height: '100%',
      }
    : {
        width: '100%',
        height: isEditMode
          ? (isTablet ? 'calc(100vh - 250px)' : `${Math.min(gridRows * baseCellSize + 32, 800)}px`)
          : (isTablet ? 'calc(100vh - 280px)' : `${Math.min(gridRows * baseCellSize + 32, 600)}px`),
        minHeight: isTablet ? '400px' : undefined,
      };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden touch-none",
        isExpanded
          ? "bg-background"
          : isEditMode
            ? "rounded-lg bg-background border border-border"
            : "rounded-lg bg-muted/20 border-2 border-dashed border-muted-foreground/20",
        isPanning ? "cursor-grabbing" : (!isEditMode && "cursor-grab"),
      )}
      style={containerStyle}
      // In edit mode: use pointer handlers from editor; in non-edit: use mouse handlers from viewport
      onMouseDown={!isEditMode ? onMouseDown : undefined}
      onMouseMove={!isEditMode ? onMouseMove : undefined}
      onMouseUp={!isEditMode ? onMouseUp : undefined}
      onMouseLeave={!isEditMode ? onMouseUp : undefined}
      onPointerDown={isEditMode ? editorHandlers?.onPointerDown : undefined}
      onPointerMove={isEditMode ? editorHandlers?.onPointerMove : undefined}
      onPointerUp={isEditMode ? editorHandlers?.onPointerUp : undefined}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={!isEditMode ? onFloorPlanClick : undefined}
    >
      <div
        className="absolute transition-transform duration-100"
        style={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
          width: `${effectiveGrid.cols * cellSize}px`,
          height: `${effectiveGrid.rows * cellSize}px`,
        }}
      >

        {/* Zone boundaries in "All" view */}
        {selectedZoneId === null && zoneBounds && zoneBounds.size > 0 && (
          <svg
            className="absolute inset-0 pointer-events-none"
            width={effectiveGrid.cols * cellSize}
            height={effectiveGrid.rows * cellSize}
            style={{ zIndex: 0 }}
          >
            {zones.map(zone => {
              const bounds = zoneBounds.get(zone.id);
              if (!bounds) return null;
              const zoneColor = zone.color || '#6366f1';
              const padding = 0.4;
              const x = (bounds.x - padding) * cellSize;
              const y = (bounds.y - padding) * cellSize;
              const w = (bounds.width + padding * 2) * cellSize;
              const h = (bounds.height + padding * 2) * cellSize;
              return (
                <g key={zone.id}>
                  <rect
                    x={x} y={y} width={w} height={h}
                    rx={8}
                    fill={zoneColor}
                    fillOpacity={0.06}
                    stroke={zoneColor}
                    strokeOpacity={0.25}
                    strokeWidth={1.5}
                    strokeDasharray="8 4"
                  />
                  <text
                    x={x + 10}
                    y={y + 18}
                    fill={zoneColor}
                    fillOpacity={0.7}
                    fontSize={12}
                    fontWeight={600}
                    fontFamily="system-ui, sans-serif"
                  >
                    {zone.name}
                  </text>
                </g>
              );
            })}
          </svg>
        )}

        {/* Zone label in single-zone view */}
        {selectedZoneId !== null && !isEditMode && (() => {
          const activeZone = zones.find(z => z.id === selectedZoneId);
          if (!activeZone) return null;
          return (
            <div
              className="absolute top-2 left-3 pointer-events-none z-10 text-xs font-semibold px-2 py-0.5 rounded"
              style={{
                color: activeZone.color || 'hsl(var(--muted-foreground))',
                backgroundColor: `${activeZone.color || '#6366f1'}12`,
              }}
            >
              {activeZone.name}
            </div>
          );
        })()}

        {/* Fixtures */}
        {displayedFixtures.map(fixture => (
          <FloorPlanFixture
            key={fixture.id}
            fixture={fixture}
            cellSize={cellSize}
            isEditMode={isEditMode}
            isSelected={selectedFixtureId === fixture.id}
            onSelect={onFixtureSelect}
            onPositionChange={onFixturePositionChange}
            onDelete={onFixtureDelete}
            gridCols={effectiveGrid.cols}
            gridRows={effectiveGrid.rows}
          />
        ))}

        {/* SVG connection lines for combined tables with reservations */}
        {!isEditMode && selectedTimeSlot && (() => {
          const lines: { x1: number; y1: number; x2: number; y2: number; key: string }[] = [];
          
          for (const ct of combinedTables) {
            if (!ct.isActive || !ct.tableIds || ct.tableIds.length < 2) continue;
            
            // Check if this combined table has an active reservation at current time
            const hasReservation = dateReservations.some(r =>
              r.combinedTableId === ct.id && r.status !== 'annulé' &&
              (r.startTime === selectedTimeSlot || r.startTime.substring(0, 5) === selectedTimeSlot)
            );
            if (!hasReservation) continue;
            
            // Get positions of component tables
            const positions = ct.tableIds
              .map(tid => displayedPositions.find(p => p.tableId === tid))
              .filter(Boolean) as typeof displayedPositions;
            
            if (positions.length < 2) continue;
            
            // Draw lines between consecutive tables
            for (let i = 0; i < positions.length - 1; i++) {
              const a = positions[i];
              const b = positions[i + 1];
              lines.push({
                x1: (a.x + a.width / 2) * cellSize,
                y1: (a.y + a.height / 2) * cellSize,
                x2: (b.x + b.width / 2) * cellSize,
                y2: (b.y + b.height / 2) * cellSize,
                key: `${ct.id}-${i}`,
              });
            }
          }
          
          if (lines.length === 0) return null;
          
          return (
            <svg
              className="absolute inset-0 pointer-events-none z-10"
              width={effectiveGrid.cols * cellSize}
              height={effectiveGrid.rows * cellSize}
            >
              {lines.map(l => (
                <line
                  key={l.key}
                  x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                  stroke="hsl(var(--primary))"
                  strokeWidth="2"
                  strokeDasharray="6 4"
                  opacity="0.5"
                />
              ))}
            </svg>
          );
        })()}

        {/* Tables */}
        {displayedPositions.map(pos => {
          const table = tables.find(t => t.id === pos.tableId);
          if (!table) return null;

          // Edit mode: use clean EditableFloorPlanTable
          if (isEditMode) {
            const currentZone = zones.find(z => z.id === pos.zoneId);
            const dragTransform = editDragTransform?.tableId === pos.tableId ? { dx: editDragTransform.dx, dy: editDragTransform.dy } : null;
            const resizeTransform = editResizeTransform?.tableId === pos.tableId ? { dw: editResizeTransform.dw, dh: editResizeTransform.dh } : null;

            return (
              <EditableFloorPlanTable
                key={`edit-${pos.tableId}`}
                position={pos}
                table={table}
                cellSize={cellSize}
                isSelected={selectedEditTableId === pos.tableId}
                dragTransform={dragTransform}
                resizeTransform={resizeTransform}
                zoneColor={currentZone?.color}
                isHighlighted={highlightedTableId === pos.tableId}
                hasOverlap={overlappingTableIds?.has(pos.tableId) ?? false}
              />
            );
          }

          // Non-edit mode: existing FloorPlanTable
          const currentSlot = timeSlots.find(ts => ts.start === selectedTimeSlot);
          const conflictOptions: TimeConflictOptions | undefined =
            reservationSettings?.bookingMode === 'time' && currentSlot
              ? {
                  bookingMode: 'time',
                  reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
                  slotEnd: currentSlot.end,
                }
              : undefined;

          const linkedInfo = getLinkedTableInfo(
            pos.tableId,
            dateReservations,
            selectedTimeSlot,
            combinedTables,
            tables,
            conflictOptions
          );

          const { status, reservation } = getTableStatus(
            pos.tableId,
            pos.type,
            dateReservations,
            selectedTimeSlot,
            table,
            currentTime,
            combinedTables,
            conflictOptions
          );

          const displayReservation = linkedInfo.reservation || reservation;
          const isCombinedTarget = combinedDropTargetInfo.has(table.id);
          const combinedTargetName = combinedDropTargetInfo.get(table.id);
          const isHighlighted = highlightedTableId === table.id;

          return (
            <FloorPlanTable
              key={`${pos.type}-${pos.tableId}`}
              position={pos}
              table={table}
              status={status}
              reservation={displayReservation}
              cellSize={cellSize}
              isEditMode={false}
              isDropTarget={isDragging}
              isValidDropTarget={validDropTargets.has(table.id)}
              isSwapDropTarget={swapDropTargets.has(table.id)}
              isCombinedDropTarget={isCombinedTarget}
              combinedDropTargetName={combinedTargetName}
              onPositionChange={onPositionChange}
              onEditReservation={onEditReservation}
              onStatusChange={onStatusChange}
              onDrop={(tableId, tableType) => {
                if (isCombinedTarget && combinedTargetName) {
                  const combinedTable = combinedTables.find(ct => ct.name === combinedTargetName);
                  if (combinedTable) {
                    onDropOnTable(combinedTable.id, 'combined');
                    return;
                  }
                }
                onDropOnTable(tableId, tableType);
              }}
              linkedTableInfo={linkedInfo.isCombined ? linkedInfo : undefined}
              gridCols={gridCols}
              gridRows={gridRows}
              isHighlighted={isHighlighted}
              checkCollision={localCheckCollision}
              freeMovementMode={freeMovementMode}
              zones={zones}
              onAssignToZone={onAssignToZone}
            />
          );
        })}

        {/* Editor toolbar for selected table */}
        {isEditMode && selectedPos && selectedTable && onShapeChange && onRotate && (
          <FloorPlanEditorToolbar
            position={selectedPos}
            table={selectedTable}
            cellSize={cellSize}
            zones={zones}
            onShapeChange={onShapeChange}
            onRotate={onRotate}
            onAssignToZone={onAssignToZone}
          />
        )}

      </div>
    </div>
  );
};
